import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Space, message, Spin, Tag, Input, Row, Col, DatePicker, Select, Skeleton, Statistic } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, FormOutlined, SearchOutlined, FilterOutlined, ProjectOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getProjectsPaginated, getUserStats, Project } from '../../services/api';
import dayjs from 'dayjs';
import './styles.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ExDataItem {
  type: string;
  is_ex: boolean | number;
  label: string;
  model: string;
  placeholder: string;
}

const Projects: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [sortedInfo, setSortedInfo] = useState<{ columnKey?: string; order?: 'ascend' | 'descend' }>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [stats, setStats] = useState({ protocolCount: 0, projectCount: 0, deviceCount: 0 });
  const navigate = useNavigate();

  const fetchProjects = async (
    page: number = 1,
    pageSize: number = 10,
    searchValue?: string,
    statusFilter?: number | null,
    dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  ) => {
    try {
      setLoading(true);
      let startDate: string | undefined;
      let endDate: string | undefined;
      if (dateRange?.[0]) {
        startDate = dateRange[0].startOf('day').toISOString();
      }
      if (dateRange?.[1]) {
        endDate = dateRange[1].endOf('day').toISOString();
      }
      const response = await getProjectsPaginated(
        page,
        pageSize,
        searchValue,
        statusFilter ?? undefined,
        startDate,
        endDate,
      );
      if (response.data.code === 0 && response.data.data) {
        setProjects(response.data.data.data);
        setPagination({
          current: page,
          pageSize,
          total: response.data.data.total
        });
        // 更新协议数量
        setStats(prev => ({
          ...prev,
          protocolCount: response.data.data?.total || 0
        }));
      } else {
        message.error(response.data.msg || '获取授权协议列表失败');
      }
    } catch (error) {
      console.error('获取授权协议列表错误:', error);
      message.error('获取授权协议列表失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getUserStats();
      if (response.data.code === 0 && response.data.data) {
        setStats(prev => ({
          ...prev,
          projectCount: response.data.data?.projectCount || 0,
          deviceCount: response.data.data?.deviceCount || 0
        }));
      }
    } catch (error) {
      console.error('获取统计信息错误:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, []);

  // Server-side search/filter - no client-side filtering needed
  const handleSearch = () => {
    // Reset to page 1 when searching
    fetchProjects(1, pagination.pageSize, searchValue, statusFilter, dateRange);
  };

  const handleResetSearch = () => {
    setSearchValue('');
    setStatusFilter(null);
    setDateRange(null);
    setSortedInfo({});
  };

  const handleTableChange = (pag: any, filters: any, sorter: any) => {
    setSortedInfo({
      columnKey: sorter.field,
      order: sorter.order,
    });
    // 调用分页接口获取新页面数据（保持当前筛选条件）
    fetchProjects(
      pag.current,
      pag.pageSize,
      searchValue,
      statusFilter,
      dateRange,
    );
  };

  const handleRefresh = () => {
    fetchProjects(
      pagination.current,
      pagination.pageSize,
      searchValue,
      statusFilter,
      dateRange,
    );
    fetchStats();
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return { text: '禁用', color: 'red' };
      case 1:
        return { text: '启用', color: 'green' };
      default:
        return { text: '未知', color: 'gray' };
    }
  };
  const columns = [
    {
      title: '协议ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: '协议名称',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      render: (text: string, record: Project) => (
        <a onClick={() => navigate(`/protocol-projects/${record.id}`, { state: { protocolTitle: record.name } })}>{text}</a>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: '启用', value: 1 },
        { text: '禁用', value: 0 },
      ],
      render: (status: number) => {
        const { text, color } = getStatusText(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '项目数',
      dataIndex: 'projectCount',
      key: 'projectCount',
      width: 100,
      sorter: true,
      render: (count: number) => (
        <Tag color="blue">{count ?? 0}</Tag>
      ),
    },
    {
      title: '设备数',
      dataIndex: 'deviceCount',
      key: 'deviceCount',
      width: 100,
      sorter: true,
      render: (count: number) => (
        <Tag color="green">{count ?? 0}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'addTime',
      key: 'addTime',
      width: 180,
      sorter: true,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Project) => (
        <Space size="middle">
          <a onClick={() => navigate(`/protocol-projects/${record.id}`, { state: { protocolTitle: record.title } })}>查看</a>
          <a onClick={() => navigate(`/project-edit/${record.id}`)}>编辑</a>
        </Space>
      ),
    },
  ];

  return (
    <div className="projects-container">
      <Card className="projects-card">
        <div className="projects-header">
          <Title level={2}>授权协议列表</Title>
          <Space wrap>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => navigate('/project-create')}
            >
              新建授权协议
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* Statistics Section */}
        <div className="projects-stats-section">
          <div className="stats-card">
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <FormOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.protocolCount}</div>
                <div className="stat-title">协议总数</div>
              </div>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <ProjectOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.projectCount}</div>
                <div className="stat-title">项目总数</div>
              </div>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <AppstoreOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.deviceCount}</div>
                <div className="stat-title">设备总数</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filter Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} sm={12} md={6} lg={5}>
              <Search
                placeholder="搜索协议ID或协议名称"
                allowClear
                enterButton={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={handleSearch}
              />
            </Col>
            <Col xs={24} sm={12} md={5} lg={4}>
              <Select
                placeholder="状态筛选"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value={1}>启用</Option>
                <Option value={0}>禁用</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={7} lg={6}>
              <RangePicker
                placeholder={['开始日期', '结束日期']}
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <Space className="projects-filter-actions" wrap>
                <Button icon={<FilterOutlined />} onClick={handleResetSearch}>
                  重置筛选
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  查询
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        
        <Spin spinning={loading}>
          {loading && projects.length === 0 ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Table 
              dataSource={projects} 
              columns={columns} 
              rowKey="id"
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                simple: window.innerWidth <= 576,
              }}
              size="small"
            />
          )}
        </Spin>

        <div className="projects-platform-footer">
          <Text type="secondary">智趣智慧工地数据管理平台</Text>
          <br />
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            辽ICP备12014462号-7
          </a>
        </div>
      </Card>
    </div>
  );
};

export default Projects; 